import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface LandingPage {
  id: string;
  title: string;
  slug: string;
  meta_title?: string;
  meta_description?: string;
  content_json?: string;
  published: number;
  url: string;
  created_at: string;
}

interface Block {
  id: string;
  type: 'heading' | 'text' | 'image' | 'button' | 'columns' | 'divider' | 'offerings' | 'hero';
  props: Record<string, any>;
}

export default function LandingPages() {
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPage, setEditingPage] = useState<LandingPage | null>(null);
  const [showingBuilder, setShowingBuilder] = useState(false);

  // Builder state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [published, setPublished] = useState(0);
  const [blocks, setBlocks] = useState<Block[]>([]);

  useEffect(() => { loadPages(); }, []);

  async function loadPages() {
    try {
      setLoading(true);
      const res = await api.get('/api/landing-pages');
      setPages(res.data ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function newPage() {
    setShowingBuilder(true);
    setEditingPage(null);
    setTitle('');
    setSlug('');
    setMetaTitle('');
    setMetaDesc('');
    setPublished(0);
    setBlocks([]);
  }

  function editPage(page: LandingPage) {
    setShowingBuilder(true);
    setEditingPage(page);
    setTitle(page.title);
    setSlug(page.slug);
    setMetaTitle(page.meta_title ?? '');
    setMetaDesc(page.meta_description ?? '');
    setPublished(page.published);
    try {
      const parsed = JSON.parse(page.content_json ?? '[]');
      setBlocks(Array.isArray(parsed) ? parsed : []);
    } catch {
      setBlocks([]);
    }
  }

  const [saving, setSaving] = useState(false);
  const [builderError, setBuilderError] = useState('');

  async function savePage() {
    setBuilderError('');
    if (!title.trim() || !slug.trim()) {
      setBuilderError('Title and slug are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        slug,
        meta_title: metaTitle || null,
        meta_description: metaDesc || null,
        content_json: JSON.stringify(blocks),
        published,
      };
      if (editingPage) {
        await api.put(`/api/landing-pages/${editingPage.id}`, payload);
      } else {
        await api.post('/api/landing-pages', payload);
      }
      setShowingBuilder(false);
      loadPages();
    } catch (err: any) {
      setBuilderError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function deletePage(id: string) {
    if (!confirm('Delete this landing page?')) return;
    await api.del(`/api/landing-pages/${id}`);
    loadPages();
  }

  async function togglePublished(page: LandingPage) {
    await api.put(`/api/landing-pages/${page.id}`, { published: page.published ? 0 : 1 });
    loadPages();
  }

  function addBlock(type: Block['type']) {
    const defaults: Record<string, any> = {
      heading: { text: 'New Heading', level: 'h2' },
      text: { text: 'Write something here...' },
      image: { src: '', alt: '', caption: '' },
      button: { label: 'Click me', url: '#', style: 'primary' },
      columns: { blocks: [[], []] as Block[][] },
      divider: {},
      offerings: { title: 'Our Offerings' },
      hero: { headline: 'Hero Headline', subline: '', cta: 'Get Started', ctaUrl: '#' },
    };
    setBlocks([...blocks, { id: crypto.randomUUID(), type, props: defaults[type] ?? {} }]);
  }

  function updateBlock(id: string, field: string, value: any) {
    setBlocks(blocks.map((b) => b.id === id ? { ...b, props: { ...b.props, [field]: value } } : b));
  }

  function removeBlock(id: string) {
    setBlocks(blocks.filter((b) => b.id !== id));
  }

  function moveBlock(id: string, dir: -1 | 1) {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= blocks.length) return;
    const arr = [...blocks];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setBlocks(arr);
  }

  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;

  if (showingBuilder) {
    return (
      <div style={{ maxWidth: 960 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{editingPage ? 'Edit' : 'New'} Landing Page</h1>
          <button onClick={() => setShowingBuilder(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {builderError && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{builderError}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <input placeholder="Page title" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 10 }}>
            <input placeholder="Slug (e.g. /lp/summer-sale)" value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} />
            <select value={published} onChange={(e) => setPublished(parseInt(e.target.value))} style={inputStyle}>
              <option value={0}>Draft</option>
              <option value={1}>Published</option>
            </select>
          </div>
          <input placeholder="Meta title (SEO)" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} style={inputStyle} />
          <input placeholder="Meta description (SEO)" value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {(['hero','heading','text','image','button','columns','divider','offerings'] as Block['type'][]).map((t) => (
            <button key={t} onClick={() => addBlock(t)} style={pillBtn}>{`+ ${t}`}</button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {blocks.map((block) => (
            <BlockEditor key={block.id} block={block} onUpdate={updateBlock} onRemove={removeBlock} onMove={moveBlock} />
          ))}
          {blocks.length === 0 && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, border: '1px dashed #e5e7eb', borderRadius: 12 }}>No blocks yet. Click a button above to add.</div>}
        </div>

        <button onClick={savePage} disabled={saving} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, background: '#111827', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Save Landing Page'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Landing Pages</h1>
        <button onClick={newPage} style={styles.createBtn}>+ New Landing Page</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
        {pages.map((page) => (
          <div key={page.id} style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>{page.title}</h3>
              <span style={styles.badge(publishedColor(page.published))}>{page.published ? 'Published' : 'Draft'}</span>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>/{page.slug}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{page.meta_title ?? 'No meta title'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => editPage(page)} style={styles.smallBtn}>Edit</button>
              <button onClick={() => togglePublished(page)} style={styles.smallBtn}>{page.published ? 'Unpublish' : 'Publish'}</button>
              <button onClick={() => deletePage(page.id)} style={{ ...styles.smallBtn, color: '#dc2626' }}>Delete</button>
            </div>
          </div>
        ))}
        {pages.length === 0 && <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#9ca3af', padding: 60 }}>No landing pages yet.</div>}
      </div>
    </div>
  );
}

function BlockEditor({ block, onUpdate, onRemove, onMove }: { block: Block; onUpdate: (id: string, f: string, v: any) => void; onRemove: (id: string) => void; onMove: (id: string, dir: -1 | 1) => void }) {
  const { id, type, props } = block;
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>{type}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => onMove(id, -1)} style={iconBtn}>↑</button>
          <button onClick={() => onMove(id, 1)} style={iconBtn}>↓</button>
          <button onClick={() => onRemove(id)} style={iconBtn}>×</button>
        </div>
      </div>
      {type === 'heading' && (
        <>
          <select value={props.level ?? 'h2'} onChange={(e) => onUpdate(id, 'level', e.target.value)} style={inputStyle}>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
          </select>
          <input value={props.text ?? ''} onChange={(e) => onUpdate(id, 'text', e.target.value)} placeholder="Heading text" style={inputStyle} />
        </>
      )}
      {type === 'text' && (
        <textarea value={props.text ?? ''} onChange={(e) => onUpdate(id, 'text', e.target.value)} placeholder="Text content" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      )}
      {type === 'image' && (
        <>
          <input value={props.src ?? ''} onChange={(e) => onUpdate(id, 'src', e.target.value)} placeholder="Image URL" style={inputStyle} />
          <input value={props.alt ?? ''} onChange={(e) => onUpdate(id, 'alt', e.target.value)} placeholder="Alt text" style={inputStyle} />
          <input value={props.caption ?? ''} onChange={(e) => onUpdate(id, 'caption', e.target.value)} placeholder="Caption" style={inputStyle} />
        </>
      )}
      {type === 'button' && (
        <>
          <input value={props.label ?? ''} onChange={(e) => onUpdate(id, 'label', e.target.value)} placeholder="Button label" style={inputStyle} />
          <input value={props.url ?? ''} onChange={(e) => onUpdate(id, 'url', e.target.value)} placeholder="URL" style={inputStyle} />
          <select value={props.style ?? 'primary'} onChange={(e) => onUpdate(id, 'style', e.target.value)} style={inputStyle}>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="outline">Outline</option>
          </select>
        </>
      )}
      {type === 'hero' && (
        <>
          <input value={props.headline ?? ''} onChange={(e) => onUpdate(id, 'headline', e.target.value)} placeholder="Headline" style={inputStyle} />
          <input value={props.subline ?? ''} onChange={(e) => onUpdate(id, 'subline', e.target.value)} placeholder="Subline" style={inputStyle} />
          <input value={props.cta ?? ''} onChange={(e) => onUpdate(id, 'cta', e.target.value)} placeholder="CTA label" style={inputStyle} />
          <input value={props.ctaUrl ?? ''} onChange={(e) => onUpdate(id, 'ctaUrl', e.target.value)} placeholder="CTA URL" style={inputStyle} />
        </>
      )}
      {type === 'offerings' && (
        <input value={props.title ?? ''} onChange={(e) => onUpdate(id, 'title', e.target.value)} placeholder="Section title" style={inputStyle} />
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit', display: 'block', width: '100%', marginBottom: 6 };
const pillBtn: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { width: 24, height: 24, borderRadius: 4, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1 };

function publishedColor(p: number) { return p ? { bg: '#dcfce7', text: '#16a34a' } : { bg: '#f3f4f6', text: '#6b7280' }; }
const styles = {
  createBtn: { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' } as React.CSSProperties,
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 } as React.CSSProperties,
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 } as React.CSSProperties,
  badge: (c: { bg: string; text: string }) => ({ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: c.bg, color: c.text } as React.CSSProperties),
  smallBtn: { fontSize: 12, background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' } as React.CSSProperties,
};
